import React, { useContext, useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Jumbotron from '../../../../assets/img/jumbotron_05.png';
import { Button } from '..';
import { useTranslation } from '../../../../shared/hooks';
import * as translations from './intl';
import { GeneralContext } from '../../../../shared/contexts/GeneralContext.context';
import CallIcon from '@mui/icons-material/Call';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { Container } from './styles';
import { ConfirmationModal } from '../../../../components';
import {
  getPartnerId,
  getLanguage,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../../../shared/constants/globalConstants';

const BgContainer = styled('div')(({ theme }) => ({
  color: theme.palette.text.main,
  backgroundImage: `url(${Jumbotron})`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
}));

const ResponsiveContainer = styled('div')(({ theme }) => ({
  color: theme.palette.text.main,
  backgroundImage: `url(${Jumbotron})`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  height: '30em',
}));

const StyledCard = styled(Card)({
  marginTop: '-25em',
  marginBottom: '3em',
  marginLeft: '0.5em',
  marginRight: '0.5em',
  maxWidth: '40em',
  padding: '0em 2em 0em 2em',
});

const Title = styled('h1')(({ theme }) => ({
  fontFamily: 'Publico Headline',
  fontSize: '50px',
  [theme.breakpoints.down('md')]: {
    fontSize: '30px !important',
  },
}));

const SubtitleB = styled('p')({
  fontFamily: 'Publico Headline',
  fontSize: '16px',
  fontWeight: '700',
});

const CommonText = styled('p')(({ theme }) => ({
  fontFamily: 'Source Sans Pro',
  fontSize: '20px',
  fontWeight: '400',
  margin: '0 0 20px 0',
  lineHeight: '25px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px !important',
    lineHeight: '14px !important',
  },
}));

const TopContainer = styled(Container)({
  width: '34em !important',
  textAlign: 'justify',
});

const TextWhite = { color: 'white' };

function RequestPhoneButton({ label, onClick }) {
  return (
    <Button
      text={label}
      variant="contained"
      onClick={() => onClick('phone')}
      sx={{
        fontFamily: 'Source Sans Pro',
        textTransform: 'uppercase',
        color: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: '#e0e0e0',
        boxShadow:
          '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
        '&:hover': {
          backgroundColor: '#d5d5d5',
        },
      }}
      icon={<CallIcon />}
    />
  );
}

function RequestVideoButton({ label, onClick }) {
  return (
    <Button
      text={label}
      variant="contained"
      color="primary"
      onClick={() => onClick('video')}
      icon={<VideocamOutlinedIcon />}
      sx={{
        marginBottom: '1em',
        fontFamily: 'Source Sans Pro',
        textTransform: 'uppercase',
      }}
    />
  );
}

const hostname = window.location.hostname;

function isGenericDomain() {
  return (
    hostname === import.meta.env.VITE_DOMAIN_GENERIC ||
    hostname === import.meta.env.VITE_DOMAIN_TRIPUK ||
    hostname === import.meta.env.VITE_DOMAIN_WWIIS
  );
}

function isUsDomain() {
  return (
    hostname === import.meta.env.VITE_DOMAIN_US ||
    hostname === import.meta.env.VITE_DOMAIN_US_METLIFE ||
    hostname === import.meta.env.VITE_DOMAIN_US_CHUBB ||
    hostname === import.meta.env.VITE_DOMAIN_USTI
  );
}

function AccessCodeModal({ open, onClose, intl }) {
  if (isGenericDomain()) {
    return (
      <ConfirmationModal
        open={open}
        handleClose={() => onClose('cross')}
        handleAction={() => onClose('ok')}
        title={intl.ACCESS_CODE_TITLE}
        text={intl.ACCESS_CODE_TEXT}
        okButton
      />
    );
  }

  if (isUsDomain()) {
    return (
      <ConfirmationModal
        open={open}
        handleClose={() => onClose('cross')}
        handleAction={() => onClose('ok')}
        title={intl.ACCESS_CODE_US_TITLE}
        text={intl.ACCESS_CODE_US_TEXT}
        okButton
      />
    );
  }

  return null;
}

export default function Banner() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { userData, isLogged } = useContext(GeneralContext);
  const [openConfirm, setOpenConfirm] = useState(true);
  const { locale: intl, idiom } = useTranslation({
    translationsFile: translations,
  });

  useEffect(() => {
    if (isLogged && !userData?.isRegisteredAmos) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        element_displayed: 'popup',
        element_name: 'access code',
        event: 'display',
      });
    }
  }, [isLogged, userData]);

  const {
    REQUEST_VIDEO_CALL_BUTTON,
    REQUEST_PHONE_CALL_BUTTON,
    DOCTOR_PLEASE,
    POWERED_BY,
    SERVICE,
    SPEAK,
    PLEASE_CONTACT,
  } = intl;

  const closeConfirmation = (elementClicked) => {
    setOpenConfirm(false);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      zone: 'popup - access code',
      element_clicked: elementClicked,
      event: 'navigation',
    });
  };

  const handleButtonsBanner = async (path) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      zone: 'cover',
      element_clicked: `${path} consultation`,
      service_category: 'health',
      event: 'navigation',
    });

    if (isLogged && userData?.isRegisteredAmos) {
      navigate(`/patient-selection?type=${path}`);
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'patient selection',
        funnel_step: '1',
        step_name: 'request appointment',
        event: 'pageview',
      });
    }

    if (isLogged && userData?.isRegisteredAmos === false) {
      navigate(`/lets-start?type=${path}`);
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'register doctor please',
        funnel_step: '0.1',
        step_name: 'register doctor please',
        event: 'pageview',
      });
    }

    if (!isLogged) {
      navigate(`/login?lang=${idiom.substr(0, 2)}`);
    }
  };

  const accessCodeModal = isLogged && !userData?.isRegisteredAmos && (
    <AccessCodeModal
      open={openConfirm}
      onClose={closeConfirmation}
      intl={intl}
    />
  );

  const actionButtons = userData?.email_verified && (
    <>
      <Grid size={{ md: 12, xs: 12 }}>
        <RequestVideoButton
          label={REQUEST_VIDEO_CALL_BUTTON}
          onClick={handleButtonsBanner}
        />
      </Grid>
      <Grid size={{ md: 12, xs: 12 }}>
        <RequestPhoneButton
          label={REQUEST_PHONE_CALL_BUTTON}
          onClick={handleButtonsBanner}
        />
      </Grid>
    </>
  );

  return (
    <div data-testid="banner">
      {isDesktop ? (
        <BgContainer>
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <TopContainer>
              <Title sx={TextWhite}>{DOCTOR_PLEASE}</Title>
              <SubtitleB sx={TextWhite}>{POWERED_BY}</SubtitleB>
              <CommonText sx={TextWhite}>{SPEAK}</CommonText>
              <CommonText
                sx={TextWhite}
              >{`${SERVICE} ${PLEASE_CONTACT}`}</CommonText>
              {accessCodeModal}
              {actionButtons}
            </TopContainer>
          </Grid>
        </BgContainer>
      ) : (
        <>
          <ResponsiveContainer />
          <Grid container justifyContent="center">
            <StyledCard>
              <CardContent sx={{ maxWidth: '40em' }}>
                <Title>{DOCTOR_PLEASE}</Title>
                <SubtitleB>{POWERED_BY}</SubtitleB>
                <CommonText>{SPEAK}</CommonText>
                <CommonText>{`${SERVICE} ${PLEASE_CONTACT}`}</CommonText>
                {accessCodeModal}
                {actionButtons}
              </CardContent>
            </StyledCard>
          </Grid>
        </>
      )}
    </div>
  );
}
